import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import DecimalInput from "./DecimalInput";
import IntegerInput from "./IntegerInput";
import MaskedInput from "./MaskedInput";
import TableField from "./TableField";
import { evaluateVisibilityExpression } from "../utils/safeExpression";

const INPUT_CLASSES =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200";

function normalizeOptions(options) {
  if (!Array.isArray(options)) return [];
  return options
    .map((option, index) => {
      if (option && typeof option === "object") {
        const value = option.value ?? option.id ?? option.key ?? option.label ?? option.name;
        const label = option.label ?? option.name ?? option.displayValue ?? value;
        if (value === undefined || value === null) return null;
        return { value: String(value), label: String(label ?? value) };
      }
      if (option === undefined || option === null) return null;
      return { value: String(option), label: String(option), key: index };
    })
    .filter(Boolean);
}

function isEmptyValue(value) {
  return (
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}

export default function ScheduleInitialValuesEditor({
  processId,
  value,
  onChange,
}) {
  const [definition, setDefinition] = useState(null);
  const [loading, setLoading] = useState(Boolean(processId));
  const [error, setError] = useState("");
  const [linkedFieldOptions, setLinkedFieldOptions] = useState({});
  const [linkedFieldLoading, setLinkedFieldLoading] = useState({});
  const [linkedFieldErrors, setLinkedFieldErrors] = useState({});
  const values = value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};

  useEffect(() => {
    let active = true;
    if (!processId) {
      setDefinition(null);
      setLoading(false);
      setError("");
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setError("");
    api
      .get(`/processes/${processId}/schedule-form`)
      .then((response) => {
        if (!active) return;
        setDefinition(response.data || null);
      })
      .catch((requestError) => {
        if (!active) return;
        setDefinition(null);
        setError(
          requestError?.response?.data?.error ||
            "Não foi possível carregar os campos deste processo.",
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [processId]);

  const fields = useMemo(
    () => (Array.isArray(definition?.schema?.fields) ? definition.schema.fields : []),
    [definition],
  );
  const groups = useMemo(
    () => (Array.isArray(definition?.schema?.groups) ? definition.schema.groups : []),
    [definition],
  );

  useEffect(() => {
    const linkedFields = fields.filter(
      (field) =>
        String(field?.type || "").toLowerCase() === "linkedfield" &&
        field?.schedulePrefillSupported !== false &&
        field?.linkedFieldId,
    );
    if (!linkedFields.length) {
      setLinkedFieldOptions({});
      setLinkedFieldLoading({});
      setLinkedFieldErrors({});
      return undefined;
    }

    let active = true;
    setLinkedFieldOptions({});
    setLinkedFieldErrors({});
    setLinkedFieldLoading(
      Object.fromEntries(linkedFields.map((field) => [field.id, true])),
    );

    Promise.all(
      linkedFields.map(async (field) => {
        try {
          const { data } = await api.get("/forms/linked-field/options", {
            params: {
              linkedProcessId: field.linkedProcessId || undefined,
              linkedFormId: field.linkedFormId || undefined,
              linkedFieldId: field.linkedFieldId,
              sourceStatus: field.sourceStatus || "completed",
            },
          });
          return {
            fieldId: field.id,
            options: Array.isArray(data?.options) ? data.options : [],
            error: "",
          };
        } catch (requestError) {
          return {
            fieldId: field.id,
            options: [],
            error:
              requestError?.response?.data?.error ||
              "Não foi possível carregar as opções vinculadas.",
          };
        }
      }),
    ).then((results) => {
      if (!active) return;
      setLinkedFieldOptions(
        Object.fromEntries(results.map((result) => [result.fieldId, result.options])),
      );
      setLinkedFieldErrors(
        Object.fromEntries(
          results
            .filter((result) => result.error)
            .map((result) => [result.fieldId, result.error]),
        ),
      );
      setLinkedFieldLoading({});
    });

    return () => {
      active = false;
    };
  }, [fields]);

  const visibleFields = useMemo(
    () => fields.filter((field) => evaluateVisibilityExpression(field?.visibleWhen, values)),
    [fields, values],
  );
  const supportedFields = useMemo(
    () => visibleFields.filter((field) => field.schedulePrefillSupported !== false),
    [visibleFields],
  );
  const unsupportedCount = visibleFields.length - supportedFields.length;
  const configuredCount = Object.keys(values).filter(
    (key) => !isEmptyValue(values[key]),
  ).length;

  const updateField = (fieldId, nextValue) => {
    const next = { ...values };
    if (isEmptyValue(nextValue)) {
      delete next[fieldId];
    } else {
      next[fieldId] = nextValue;
    }
    onChange(next);
  };

  const handleInputChange = (fieldId) => (event) => {
    updateField(fieldId, event?.target?.value ?? "");
  };

  const renderControl = (field) => {
    const currentValue = values[field.id] ?? "";
    const type = String(field.type || "text").toLowerCase();

    if (type === "linkedfield") {
      const options = Array.isArray(linkedFieldOptions[field.id])
        ? linkedFieldOptions[field.id]
        : [];
      const multiple = Boolean(field.multiple || field.allowMultiple);
      const selectedValue = multiple
        ? Array.isArray(currentValue)
          ? currentValue
          : currentValue
            ? [currentValue]
            : []
        : currentValue;
      const isLoadingOptions = Boolean(linkedFieldLoading[field.id]);
      const fieldError = linkedFieldErrors[field.id] || "";

      return (
        <div className="space-y-1.5">
          <select
            id={`schedule-initial-${field.id}`}
            value={selectedValue}
            multiple={multiple}
            disabled={isLoadingOptions}
            onChange={(event) => {
              if (multiple) {
                updateField(
                  field.id,
                  Array.from(event.target.selectedOptions).map(
                    (option) => option.value,
                  ),
                );
                return;
              }
              updateField(field.id, event.target.value);
            }}
            className={`${INPUT_CLASSES} appearance-none disabled:cursor-wait disabled:bg-slate-100`}
          >
            {!multiple && (
              <option value="">
                {isLoadingOptions
                  ? "Carregando opções..."
                  : "Não preencher agora"}
              </option>
            )}
            {options.map((option) => (
              <option key={option.key} value={option.key}>
                {option.displayValue}
                {option.requestId ? ` (#${option.requestId})` : ""}
              </option>
            ))}
          </select>
          {!isLoadingOptions && !fieldError && options.length === 0 && (
            <p className="text-xs text-slate-500">
              Nenhum registro vinculado acessível foi encontrado.
            </p>
          )}
          {fieldError && (
            <p className="text-xs text-rose-600">{fieldError}</p>
          )}
          {multiple && (
            <p className="text-xs text-slate-500">
              Use Ctrl ou Command para selecionar mais de uma opção.
            </p>
          )}
        </div>
      );
    }

    if (type === "table") {
      return (
        <TableField
          field={field}
          value={Array.isArray(currentValue) ? currentValue : []}
          onChange={(rows) => updateField(field.id, rows)}
          inputClassName={INPUT_CLASSES}
        />
      );
    }

    if (type === "list" || type === "select" || type === "dropdown" || type === "radio") {
      const options = normalizeOptions(field.options);
      const multiple = Boolean(field.allowMultiple || field.multiple);
      return (
        <select
          id={`schedule-initial-${field.id}`}
          value={multiple ? (Array.isArray(currentValue) ? currentValue : []) : currentValue}
          multiple={multiple}
          onChange={(event) => {
            if (multiple) {
              updateField(
                field.id,
                Array.from(event.target.selectedOptions).map((option) => option.value),
              );
              return;
            }
            updateField(field.id, event.target.value);
          }}
          className={INPUT_CLASSES}
        >
          {!multiple && <option value="">Não preencher agora</option>}
          {options.map((option, index) => (
            <option key={`${option.value}-${index}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    if (type === "checkbox" || type === "boolean") {
      return (
        <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <input
            id={`schedule-initial-${field.id}`}
            type="checkbox"
            checked={currentValue === true}
            onChange={(event) => updateField(field.id, event.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          Marcar este campo
        </label>
      );
    }

    if (type === "decimal") {
      return (
        <DecimalInput
          id={`schedule-initial-${field.id}`}
          value={currentValue}
          decimalPlaces={field.decimalPlaces || 2}
          onChange={handleInputChange(field.id)}
          className={INPUT_CLASSES}
        />
      );
    }

    if (type === "number" || type === "integer") {
      return (
        <IntegerInput
          id={`schedule-initial-${field.id}`}
          value={currentValue}
          onChange={handleInputChange(field.id)}
          className={INPUT_CLASSES}
        />
      );
    }

    if (["currency", "phone", "cpf", "cnpj", "cep"].includes(type)) {
      return (
        <MaskedInput
          id={`schedule-initial-${field.id}`}
          mask={type}
          value={currentValue}
          onChange={handleInputChange(field.id)}
          className={INPUT_CLASSES}
        />
      );
    }

    if (type === "textarea") {
      return (
        <textarea
          id={`schedule-initial-${field.id}`}
          value={currentValue}
          rows={Math.max(3, Number(field.rows) || 3)}
          onChange={handleInputChange(field.id)}
          placeholder="Deixe em branco para preencher quando a solicitação for criada"
          className={`${INPUT_CLASSES} resize-y`}
        />
      );
    }

    if (type === "email") {
      return (
        <div className="space-y-1.5">
          <input
            id={`schedule-initial-${field.id}`}
            type="email"
            multiple
            value={currentValue}
            onChange={handleInputChange(field.id)}
            placeholder="email@exemplo.org.br, outro@exemplo.org.br"
            className={INPUT_CLASSES}
          />
          <p className="text-xs text-slate-500">
            Para mais de um destinatário, separe os e-mails por vírgula.
          </p>
        </div>
      );
    }

    const inputType = ["date", "time"].includes(type) ? type : "text";
    return (
      <input
        id={`schedule-initial-${field.id}`}
        type={inputType}
        value={currentValue}
        onChange={handleInputChange(field.id)}
        placeholder="Deixe em branco para preencher depois"
        className={INPUT_CLASSES}
      />
    );
  };

  const sections = useMemo(() => {
    const result = groups
      .map((group) => ({
        id: group.id,
        label: group.label || "Campos",
        fields: supportedFields.filter((field) => field.groupId === group.id),
      }))
      .filter((group) => group.fields.length > 0);
    const knownGroupIds = new Set(groups.map((group) => group.id));
    const ungrouped = supportedFields.filter(
      (field) => !field.groupId || !knownGroupIds.has(field.groupId),
    );
    if (ungrouped.length > 0) {
      result.push({
        id: "__ungrouped__",
        label: groups.length > 0 ? "Outros campos" : "Campos do formulário",
        fields: ungrouped,
      });
    }
    return result;
  }, [groups, supportedFields]);

  return (
    <div className="space-y-4 border-t border-slate-200 pt-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-slate-800">Dados pré-definidos</p>
            {configuredCount > 0 && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                {configuredCount} configurado{configuredCount === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">
            Estes dados serão incluídos automaticamente na solicitação agendada.
            Os demais campos poderão ser preenchidos normalmente depois.
          </p>
        </div>
        {configuredCount > 0 && (
          <button
            type="button"
            onClick={() => onChange({})}
            className="w-fit rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
          >
            Limpar dados
          </button>
        )}
      </div>

      {loading && (
        <p className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
          Carregando campos disponíveis...
        </p>
      )}

      {error && (
        <p className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {error}
        </p>
      )}

      {!loading && !error && definition?.hasForm === false && (
        <p className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Este processo não possui formulário inicial. O agendamento continuará funcionando
          apenas com a data e a recorrência.
        </p>
      )}

      {!loading && !error && definition?.hasForm && sections.length === 0 && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Este formulário não possui campos compatíveis com preenchimento antecipado.
        </p>
      )}

      {sections.map((section) => (
        <section key={section.id} className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <h4 className="text-sm font-semibold text-slate-800">{section.label}</h4>
          <div className="grid gap-4 sm:grid-cols-2">
            {section.fields.map((field) => (
              <div
                key={field.id}
                className={`space-y-2 ${field.type === "table" || field.type === "textarea" ? "sm:col-span-2" : ""}`}
              >
                <div>
                  <label
                    htmlFor={`schedule-initial-${field.id}`}
                    className="text-sm font-medium text-slate-700"
                  >
                    {field.label}
                  </label>
                  {field.description && (
                    <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                      {field.description}
                    </p>
                  )}
                </div>
                {renderControl(field)}
              </div>
            ))}
          </div>
        </section>
      ))}

      {!loading && !error && unsupportedCount > 0 && (
        <p className="text-xs leading-relaxed text-slate-500">
          {unsupportedCount} campo{unsupportedCount === 1 ? "" : "s"} de anexo,
          assinatura, documento ou seleção dinâmica deverá{unsupportedCount === 1 ? "" : "ão"} ser
          preenchido{unsupportedCount === 1 ? "" : "s"} após a criação da solicitação.
        </p>
      )}
    </div>
  );
}
